import { useState } from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { Alert, Button, Card, Col, Form, Input, Progress, Result, Row, Select, Typography, Upload } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cardmargin, grayheader } from "../../CSSReferences";
import {
    ADMIN_ROLE, BRANCHES, Branch, EMPTY_FORM, EmergencyForm, FormProblem, UploadOutcome,
    formatBytes, judgeForm, outcomeOf, progressPercent, versionTaken,
} from "../typing/emergencyUpload";
import { fetchReleases, sendEmergencyUpload } from "../thunk/upload";

export interface EmergencyUploadViewProps {
    id: string;
    url?: string;
    token: string;
    role: number;
}

type Translate = (id: string, values?: Record<string, string | number>) => string;

/**
 * Emergency upload of a release ("Notfall-Upload", route /UpdateManager).
 *
 * The normal route is the release pipeline; this page is for when it cannot
 * deliver. The package is streamed to the hub, which checks it as the
 * pipeline does and registers it INACTIVE - it is offered to no spoke until
 * it is activated on the Updates page. Admin only; the hub enforces that.
 */
export function EmergencyUploadView(props: EmergencyUploadViewProps) {
    const intl = useIntl();
    const t: Translate = (id, values) => intl.formatMessage({ id, defaultMessage: id }, values);

    const [form, setForm] = useState<EmergencyForm>(EMPTY_FORM);
    const [file, setFile] = useState<File | null>(null);
    const [touched, setTouched] = useState(false);
    const [percent, setPercent] = useState<number | null>(null);
    const [outcome, setOutcome] = useState<UploadOutcome | null>(null);

    if (props.role !== ADMIN_ROLE) {
        return <Alert type="warning" showIcon title={t("EmergencyUpload:AdminOnly")} style={cardmargin} />;
    }

    const problems = judgeForm(form);
    const uploading = percent !== null && outcome === null;
    const update = (change: Partial<EmergencyForm>) => {
        setForm({ ...form, ...change });
        setOutcome(null);
    };
    const shows = (problem: FormProblem) => touched && problems.includes(problem);
    const problemText = (...candidates: FormProblem[]) =>
        candidates.filter(shows).map((p) => t("EmergencyUpload:Problem:" + p))[0];

    const submit = async () => {
        setTouched(true);
        if (problems.length > 0 || file === null) return;
        setOutcome(null);
        setPercent(0);
        if (versionTaken(await fetchReleases(props.token), form.software, form.version)) {
            setOutcome({ kind: "rejected", status: 409, code: "version-exists", message: "Version " + form.version.trim() + " exists" });
            return;
        }
        const answer = await sendEmergencyUpload(props.token, form, file,
            (loaded, total) => setPercent(progressPercent(loaded, total)));
        setOutcome(outcomeOf(answer.status, answer.body));
    };

    const restart = () => {
        setForm(EMPTY_FORM);
        setFile(null);
        setTouched(false);
        setPercent(null);
        setOutcome(null);
    };

    return (
        <Row gutter={16}>
            <Col xs={24} xl={{ span: 16, offset: 4 }}>
                <Card title={<><FontAwesomeIcon icon={["fas", "triangle-exclamation"]} /> {t("EmergencyUpload:Title")}</>}
                      style={cardmargin} styles={{ header: grayheader }}>
                    <Alert type="warning" showIcon title={t("EmergencyUpload:Warning")} style={{ marginBottom: 16 }} />

                    {outcome?.kind === "accepted"
                        ? <Accepted t={t} outcome={outcome} onAnother={restart} />
                        : <Form layout="vertical" component="div" disabled={uploading}>
                            <Form.Item label={t("EmergencyUpload:File")} required
                                       validateStatus={problemText("file-missing", "file-empty", "file-too-large") ? "error" : undefined}
                                       help={problemText("file-missing", "file-empty", "file-too-large")}>
                                <Upload maxCount={1} showUploadList={false}
                                        beforeUpload={(chosen: File) => {
                                            setFile(chosen);
                                            update({ fileName: chosen.name, fileSize: chosen.size });
                                            return false;
                                        }}>
                                    <Button icon={<FontAwesomeIcon icon={["fas", "file-arrow-up"]} />}>
                                        {t("EmergencyUpload:ChooseFile")}
                                    </Button>
                                </Upload>
                                {form.fileName !== null &&
                                    <Typography.Text style={{ marginLeft: 12 }}>
                                        {form.fileName} ({formatBytes(form.fileSize ?? 0)})
                                    </Typography.Text>}
                            </Form.Item>

                            <Row gutter={16}>
                                <Col xs={24} md={8}>
                                    <Form.Item label={t("EmergencyUpload:Software")}
                                               validateStatus={shows("software-invalid") ? "error" : undefined}
                                               help={problemText("software-invalid")}>
                                        <Input value={form.software} onChange={(e) => update({ software: e.target.value })} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item label={t("EmergencyUpload:Version")} required
                                               validateStatus={problemText("version-missing", "version-invalid") ? "error" : undefined}
                                               help={problemText("version-missing", "version-invalid")}>
                                        <Input value={form.version} placeholder="7.793"
                                               onChange={(e) => update({ version: e.target.value })} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item label={t("EmergencyUpload:Branch")} required
                                               validateStatus={shows("branch-missing") ? "error" : undefined}
                                               help={problemText("branch-missing")}>
                                        <Select<Branch> value={form.branch ?? undefined}
                                                        placeholder={t("EmergencyUpload:ChooseBranch")}
                                                        onChange={(branch) => update({ branch })}
                                                        options={BRANCHES.map((b) => ({ value: b, label: b }))} />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item label={t("EmergencyUpload:ExpectedSha256")}
                                       validateStatus={shows("sha256-invalid") ? "error" : undefined}
                                       help={problemText("sha256-invalid") ?? t("EmergencyUpload:ExpectedSha256Hint")}>
                                <Input value={form.sha256} allowClear spellCheck={false}
                                       style={{ fontFamily: "monospace" }}
                                       onChange={(e) => update({ sha256: e.target.value })} />
                            </Form.Item>

                            {percent !== null && outcome === null &&
                                <Progress percent={percent} status="active" style={{ marginBottom: 16 }} />}

                            {outcome?.kind === "rejected" &&
                                <Alert type="error" showIcon style={{ marginBottom: 16 }}
                                       title={t("EmergencyUpload:Refused", { status: outcome.status || "-" })}
                                       description={rejectionText(t, outcome.code, outcome.message)} />}

                            <Button type="primary" onClick={submit} loading={uploading}
                                    disabled={uploading || (touched && problems.length > 0)}>
                                {uploading ? t("EmergencyUpload:Uploading") : t("EmergencyUpload:Upload")}
                            </Button>
                        </Form>}
                </Card>
            </Col>
        </Row>
    );
}

/** A refusal in the user's language where the code is known, else the hub's own (English) message. */
function rejectionText(t: Translate, code: string, message: string): string {
    const id = "EmergencyUpload:Error:" + code;
    const translated = t(id);
    return translated === id ? message : translated + " (" + message + ")";
}

function Accepted(props: { t: Translate; outcome: Extract<UploadOutcome, { kind: "accepted" }>; onAnother: () => void }) {
    const { t, outcome } = props;
    return (
        <Result status="success"
                title={t("EmergencyUpload:Accepted", { version: outcome.version, branch: outcome.branch })}
                subTitle={<>
                    <div>{t("EmergencyUpload:Inactive")}</div>
                    <div>SHA-256: <Typography.Text code copyable>{outcome.hash}</Typography.Text></div>
                    <div>{formatBytes(outcome.size)} · {t("EmergencyUpload:Predecessor")}: {outcome.precessor ?? "-"}</div>
                </>}
                extra={[
                    <Link key="updates" to="/Updates"><Button type="primary">{t("EmergencyUpload:ToUpdates")}</Button></Link>,
                    <Button key="again" onClick={props.onAnother}>{t("EmergencyUpload:Another")}</Button>,
                ]} />
    );
}

export default EmergencyUploadView;
